import React from 'react'
import { useNavigate } from 'react-router-dom'
import OktaAuth, { AuthState, OktaAuthOptions } from '@okta/okta-auth-js'
import { Security, useOktaAuth } from '@okta/okta-react'
import { configs } from '@configs'
import { MkAuthenticationService } from '@features/authentication/services/AuthService/_authService'
import { serverUrl } from '@utils/MkEnv'
import { API } from '@constants'

export type { OktaUser, OktaMfaFactor, OktaPasswordPolicy, OktaAuthnResponse, OktaPasswordResetResponse, OktaTokenResponse } from './oktaAuthnTypes'
export { OktaResponseStatus } from './oktaAuthnTypes'

const OktaContext = React.createContext<{
  oktaAuth: OktaAuth
  oktaAuthState?: AuthState | null
}>({} as any)

export interface OktaProviderProps {
  config?: OktaAuthOptions
  children: React.ReactNode
}

const InternalOktaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { oktaAuth, authState: oktaAuthState } = useOktaAuth()

  const value = React.useMemo(() => ({ oktaAuth, oktaAuthState }), [oktaAuth, oktaAuthState])

  return <OktaContext.Provider value={value}>{children}</OktaContext.Provider>
}

export const OktaProvider: React.FC<OktaProviderProps> = ({ children, config }) => {
  const oktaConfig = config || configs.oidc
  const navigate = useNavigate()

  const isValidIssuer = oktaConfig?.issuer && !oktaConfig.issuer.includes('{your') && !oktaConfig.issuer.includes('{issuer}')

  if (!isValidIssuer) {
    return <div>{children}</div>
  }

  try {
    // okta-react requires restoreOriginalUri on <Security>.
    // It warns if restoreOriginalUri is also present in the OktaAuth config.
    // So we ensure it's NOT in the OktaAuth config and pass it only as a prop.
    const oktaAuth = React.useMemo(() => {
      const configForAuth = { ...oktaConfig } as OktaAuthOptions & { restoreOriginalUri?: unknown }
      if ('restoreOriginalUri' in configForAuth) {
        delete configForAuth.restoreOriginalUri
      }

      const auth = new OktaAuth(configForAuth)

      // okta-react's <Security> will always inject its own restoreOriginalUri wrapper into oktaAuth.options.
      // Some okta-auth-js versions initialize oktaAuth.options.restoreOriginalUri by default, which triggers
      // okta-react's "Two custom restoreOriginalUri callbacks" warning even when we don't provide one.
      // Clear it so <Security> can set it without logging.
      ;(auth as any).options.restoreOriginalUri = undefined

      return auth
    }, [oktaConfig])

    const restoreOriginalUri = React.useMemo(() => MkAuthenticationService.restoreOriginalUriBuilder(navigate), [navigate])

    return (
      <Security
        oktaAuth={oktaAuth}
        restoreOriginalUri={restoreOriginalUri}
        onAuthRequired={(oktaAuthProvided) => {
          oktaAuthProvided.signInWithRedirect({
            originalUri: '/',
            responseMode: 'query',
            responseType: 'code',
            scopes: ['openid', 'email', 'profile'],
            pkce: true,
          })
        }}>
        <InternalOktaProvider>{children}</InternalOktaProvider>
      </Security>
    )
  } catch (error) {
    return <div>{children}</div>
  }
}

export const useOkta = () => {
  const context = React.useContext(OktaContext)
  if (!context) {
    throw new Error('useOkta must be used within an OktaProvider')
  }
  return context
}

// Okta URL Utility Functions
/**
 * Extracts the Okta org domain from an issuer URL
 *
 * @param issuerUrl - The full Okta issuer URL (e.g., "https://dev-okta.wyndhamdestinations.com/oauth2/ausrc1knbrgu0Tz6k1d6")
 * @returns The org domain without the authorization server path (e.g., "https://dev-okta.wyndhamdestinations.com")
 *
 * @example
 * extractOktaOrgDomain("https://dev-okta.wyndhamdestinations.com/oauth2/ausrc1knbrgu0Tz6k1d6")
 * // Returns: "https://dev-okta.wyndhamdestinations.com"
 */
export function extractOktaOrgDomain(issuerUrl: string): string {
  if (!issuerUrl) {
    throw new Error('Issuer URL is required')
  }

  // Remove the /oauth2/{authorizationServerId} part from the issuer URL
  // This regex matches /oauth2/ followed by any characters until the end of the string
  const orgDomain = issuerUrl.replace(/\/oauth2\/[^/]+$/, '')

  if (!orgDomain || orgDomain === issuerUrl) {
    throw new Error(`Invalid Okta issuer URL format: ${issuerUrl}. Expected format: https://your-org.okta.com/oauth2/authorizationServerId`)
  }

  return orgDomain
}

// Okta API Functions
export async function authenticateWithOkta(activationToken: string): Promise<OktaAuthnResponse> {
  const oktaAuthUrl = `${serverUrl}${API}/okta/authn`

  const response = await fetch(oktaAuthUrl, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token: activationToken }),
  })

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`
    try {
      const errJson = await response.json()
      errorMessage = errJson?.errorSummary || errJson?.errorCode || errorMessage
    } catch (error) {
      // Ignore JSON parsing errors, use default error message
    }
    throw new Error(errorMessage)
  }

  const result = await response.json()

  return result
}

export async function setPasswordWithOkta(stateToken: string, password: string): Promise<OktaPasswordResetResponse> {
  const oktaAuthUrl = `${serverUrl}${API}/okta/authn/credentials/reset_password`

  const response = await fetch(oktaAuthUrl, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      stateToken,
      newPassword: password,
      revokeSessions: true,
    }),
  })

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`
    try {
      const errJson = await response.json()
      errorMessage = errJson?.errorSummary || errJson?.errorCode || errorMessage
    } catch (error) {
      // Ignore JSON parsing errors, use default error message
    }
    throw new Error(errorMessage)
  }

  const result = await response.json()

  return result
}

export async function getOktaAccessTokenDirect(username: string, password: string, clientId: string): Promise<OktaTokenResponse> {
  const formData = new URLSearchParams()
  formData.append('username', username)
  formData.append('password', password)
  formData.append('grant_type', 'password')
  formData.append('client_id', clientId)
  formData.append('scope', 'openid profile email')

  const response = await fetch(`${configs.oidc.issuer}/v1/token`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formData.toString(),
  })

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`
    try {
      const errJson = await response.json()
      errorMessage = errJson?.error_description || errJson?.error || errorMessage
    } catch (error) {
      // Ignore JSON parsing errors, use default error message
    }
    throw new Error(errorMessage)
  }

  return response.json()
}

/**
 * Initiates a password reset flow with Okta by sending a recovery email to the user.
 *
 * This function follows the Register flow pattern for Okta API calls:
 * - Uses direct fetch instead of Okta SDK for better control
 * - Includes credentials to ensure cookies are sent with cross-origin requests
 * - Properly extracts and propagates errorCode for rate limiting detection
 *
 * Password Reset Flow:
 * 1. User enters username/email in forgot password form
 * 2. This function calls Okta's recovery API → Okta sends email with reset link
 * 3. User clicks link in email → redirects to `/activate?rt={recoveryToken}`
 * 4. MkPageActivate processes the token and opens register modal at SET_PASSWORD step
 * 5. User sets new password → Register flow's SET_PASSWORD step calls Okta's reset_password API
 *
 * @param username - User's username or email address
 * @returns Promise resolving to OktaPasswordResetResponse with status and stateToken
 * @throws Error with errorCode property for rate limiting (E0000118) or other Okta errors
 *
 * @example
 * try {
 *   await initiateOktaPasswordReset('user@example.com')
 *   // Show success message - email sent
 * } catch (err) {
 *   if (err.errorCode === 'E0000118') {
 *     // Handle rate limiting - too many requests
 *   }
 * }
 */
export async function initiateOktaPasswordReset(username: string): Promise<OktaPasswordResetResponse> {
  const oktaAuthUrl = `${serverUrl}${API}/okta/authn/recovery/password`

  const response = await fetch(oktaAuthUrl, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies in cross-origin requests
    body: JSON.stringify({
      username,
      factorType: 'EMAIL',
    }),
  })

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`
    let errorCode = ''
    try {
      const errJson = await response.json()
      errorMessage = errJson?.errorSummary || errJson?.errorCode || errorMessage
      errorCode = errJson?.errorCode || ''
    } catch (error) {
      // Ignore JSON parsing errors, use default error message
    }

    // Create error with errorCode property for rate limiting detection
    const err: any = new Error(errorMessage)
    if (errorCode) {
      err.errorCode = errorCode
    }
    throw err
  }

  const result = await response.json()

  if (result.status !== OktaResponseStatus.RECOVERY_CHALLENGE) {
    throw new Error(`Error initiating password reset: ${result.status}`)
  }

  return result as OktaPasswordResetResponse
}
